<script setup lang="ts">
import { first } from "es-toolkit/compat";
import { computed, watch } from "vue";

import { useFeatureFlag } from "@/composables/feature-flag/use-feature-flag";
import { useGetSiteProgramsQuery } from "@/composables/kili/sites/queries/use-get-site-programs-query";
import { DATA_TEST_IDS } from "@/constants/data-test-ids/data-test-ids.constant";
import { ProgramId, V4HSiteId } from "@/data/common/ids";
import { SiteProgramOverview } from "@/domains/v4b/program/program.types";
import { useTypedIntl } from "@/locales/typed-intl";
import { siteDefaultProgramFeatureFlag } from "@/services/feature-flags/env-feature-flags";

import RadioButton from "primevue/radiobutton";
import Skeleton from "primevue/skeleton";

import ProgramSelectionCard from "@/components/piloting/planning/programming-range/select-program/program-selection-card.vue";

const props = defineProps<{
  siteId: V4HSiteId;
  disabled?: boolean;
}>();

const selectedProgramId = defineModel<ProgramId | undefined>("selectedProgramId", { required: true });

const { t, k } = useTypedIntl();
const { isEnvFeatureEnabled } = useFeatureFlag();

const { data: programs, isLoading } = useGetSiteProgramsQuery(props.siteId);

function handleSelectProgram(program: SiteProgramOverview) {
  if (props.disabled) return;
  selectedProgramId.value = program.id;
}

const applicablePrograms = computed(() => {
  if (!programs.value) return [];

  if (isEnvFeatureEnabled(siteDefaultProgramFeatureFlag)) {
    return programs.value.filter((program) => !program.isSiteDefault);
  }

  return programs.value.filter((program) => !program.isActive);
});

watch(
  applicablePrograms,
  (newValue) => {
    if (!newValue) return;

    if (selectedProgramId.value) {
      const maybeProgram = applicablePrograms.value.find((program) => program.id === selectedProgramId.value);
      if (maybeProgram) return;
    }

    const firstProgram = first(newValue);
    if (firstProgram) selectedProgramId.value = firstProgram.id;
  },
  { immediate: true },
);
</script>

<template>
  <div
    class="flex flex-col w-full gap-3"
    :data-test-id="DATA_TEST_IDS.components.selectProgram.component"
  >
    <template v-if="isLoading">
      <Skeleton
        v-for="i in 3"
        :data-test-id="DATA_TEST_IDS.components.selectProgram.skeleton"
        :key="i"
        class="!w-full !h-[60px]"
      />
    </template>
    <template v-else>
      <div
        v-if="!applicablePrograms.length"
        class="flex w-full h-[100px] items-center justify-center"
      >
        <span
          class="text-text-secondary"
          :data-test-id="DATA_TEST_IDS.components.selectProgram.noResultText"
        >
          {{ t(k.components.selectProgram.noResult) }}
        </span>
      </div>
      <ProgramSelectionCard
        v-for="program in applicablePrograms"
        class="cursor-pointer"
        :key="program.id"
        :program-color="program.frontDisplayColor"
        :program-id="program.id"
        :program-name="program.name"
        @click="handleSelectProgram(program)"
      >
        <RadioButton
          v-model="selectedProgramId"
          :data-test-id="DATA_TEST_IDS.components.selectProgram.programSelectionCard.radioButton"
          :disabled="props.disabled"
          name="selected-program"
          :value="program.id"
        />
      </ProgramSelectionCard>
    </template>
  </div>
</template>
